<template>
  <RangeInput
    ref="targetRef"
    :bs="{
      width: selectMode === 'time' ? 440 : selectMode === 'day' ? 290 : null,
      ...bs,
    }"
    :readonly="readonly"
    :disabled="disabled"
    :left="startStr"
    :right="endStr"
    :get-input-props="getInputProps"
    v-bind="$attrs"
    @click:clear="() => handleClear()"
    @click="() => toggle(true)"
  >
    <template #left>
      <BeerIcon
        :icon-path="CALENDAR_14"
        :grid-size="14"
        :size="14"
        :bs="{ ml: 12, mt: 9, mb: 'auto' }"
        color="mute"
      />
    </template>
    <BodyPortal :active="fixed">
      <Calendar
        v-if="isActive"
        ref="popperRef"
        :container-props="bindContainerProps"
        :show-month-cnt="2"
        :base="base"
        @click.stop="() => {}"
      >
        <template
          v-if="shortcutList.length > 0"
          #head
        >
          <NewBsBox
            :bs="{ pt: 12, pb: 4, px: 24 }"
          >
            <ButtonGroup
              :default-first="false"
              :list="shortcutList"
              active-variant="text"
              :model-value="shortcut"
              @update:modelValue="pickShortcut"
            />
          </NewBsBox>
        </template>
        <template
          v-if="selectMode === 'time'"
          #tail
        >
          <NewBsBox
            :bs="{
              display: 'flex', alignItems: 'center'
            }"
          >
            <NewBsBox :bs="{ flex: 1}">
              <ClockSelect
                :bs="{ width: 160, mx: 20, mb: 16 }"
                :popover-props="{ fixed: false }"
                v-bind="clockProps"
                :default-time="startTime.value"
                :model-value="startTime.value"
                @update:modelValue="v => handleSelectTime('start', v)"
              />
            </NewBsBox>
            <NewBsBox :bs="{ flex: 1}">
              <ClockSelect
                :bs="{ width: 160, mx: 20, mb: 16 }"
                :popover-props="{ fixed: false }"
                v-bind="clockProps"
                :default-time="endTime.value"
                :model-value="endTime.value"
                @update:modelValue="v => handleSelectTime('end', v)"
              />
            </NewBsBox>
          </NewBsBox>
        </template>
      </Calendar>
    </BodyPortal>
  </RangeInput>
</template>

<script lang="ts">
  import {
    defineComponent, PropType, computed, ref,
  } from 'vue'
  import { SelectModeType, ViewModeType } from '@xhs/yam-beer/cmp/constants'
  import {
    isDayEqual, isDayAfter, dayjs,
  } from '@xhs/yam-beer/utils/dayjs'
  import type { Dayjs } from 'dayjs'
  import { CALENDAR_14 } from '@xhs/yam-beer/cmp/constants/builtinIcon'
  import NewBsBox from '@xhs/yam-beer/cmp/primitive/NewBsBox'
  import BeerIcon from '@xhs/yam-beer/cmp/common/Icon/Icon.vue'
  import { useSelectPopover, SelectPopoverPartial } from '../../../compose/popover'
  import RangeInput from '../Input/RangeInput.vue'
  import Calendar from './Calendar/Calendar.vue'
  import {
    useDateValueRange, Model, TimeObjType,
  } from './dateUtils'
  import BodyPortal from '../../primitive/BodyPortal'
  import ButtonGroup from '../../navigation/ButtonGroup/ButtonGroup.vue'
  import ClockSelect from './ClockSelect.vue'

  const transUnit: Record<string, string> = {
    d: '天',
    M: '月',
    w: '周',
    y: '年',
  }

  /**
   * 日期 range 选择
   */
  export default defineComponent({
    name: 'DateRangePicker',
    components: {
      RangeInput,
      Calendar,
      BodyPortal,
      ButtonGroup,
      NewBsBox,
      ClockSelect,
      BeerIcon,
    },
    props: {
      /**
       * 选项 SelectButton 的 inline 样式，作为 inputProps 的快捷方式
       */
      bs: {
        type: Object,
        default: () => ({}),
      },
      readonly: {
        type: Boolean,
        default: false,
      },
      /**
       * 是否禁止选
       */
      disabled: {
        type: Boolean,
        default: false,
      },
      start: {
        type: [Object, String] as PropType<Model>,
      },
      end: {
        type: [Object, String] as PropType<Model>,
      },
      startPlaceholder: {
        type: String,
        default: '开始时间',
      },
      endPlaceholder: {
        type: String,
        default: '截止时间',
      },
      /**
       * 禁止日期
       */
      disableDay: {
        type: Function,
      },
      /**
       * 是否需要选择日期
       * 选择模式，默认选择日期
       * day | month | year | time
       */
      selectMode: {
        type: String as PropType<SelectModeType>,
        default: 'day',
      },
      allowEqual: {
        type: Boolean,
        default: true,
      },
      /**
       * 默认时间支持字符串格式 ['00:00:00', '23:59:59']
       */
      defaultTimeList: {
        type: Array as PropType<string[]>,
        default: () => (['00:00:00', '23:59:59']),
      },
      /**
       * withTime 模式下，个性化时间选择
       * 比如： mode = 继续细化选择时间的粒度
       */
      clockProps: {
        type: Object,
        default: () => ({}),
      },
      /**
       * 下拉框是否是 fixed 模式
       */
      fixed: {
        type: Boolean,
        default: false,
      },
      /**
       * 下拉框的高级选项
       */
      popoverParams: {
        type: Object as PropType<SelectPopoverPartial>,
        default: () => ({}),
      },
      /**
       * 时间快捷选择配置：
       * 比如： ['7d', '15d', '3w', '1m', '3m']
       * 传入空则不显示快捷选择
       */
      shortcutOptions: {
        type: Array,
        default: () => ([]),
      },
    },
    emits: ['update:start', 'update:end'],
    setup(props, { emit }) {
      const now = dayjs()
      const shortcutList = props.shortcutOptions.map((str: string) => {
        if (str.length < 2) {
          return null
        }
        let unit = str[str.length - 1]
        if (unit === 'm') {
          unit = 'M'
        }
        const unitText = transUnit[unit]
        if (!unitText) {
          return null
        }
        const count = Number(str.match(/\d+/)?.[0])
        if (Number.isNaN(count) || count < 1) {
          return null
        }
        return {
          name: `最近${count}${unitText}`,
          start: now.subtract(count, unit as any),
        }
      }).filter(i => !!i)
      const shortcut = ref<string|null>(null)
      const hoverDate = ref<Dayjs|null>(null)
      const {
        base,
        startState,
        endState,
        startStr,
        endStr,
        setModel,
        toModel,
        toRangeModel,
        startTime,
        endTime,
      } = useDateValueRange({
        allowEqual: props.allowEqual,
        startRef: computed(() => props.start),
        endRef: computed(() => props.end),
        selectMode: props.selectMode,
        defaultTimeList: props.defaultTimeList,
      })

      const {
        targetRef,
        popperRef,
        isActive,
        toggle,
      } = useSelectPopover({
        strategy: props.fixed ? 'fixed' : 'absolute',
        ...props.popoverParams,
        disabled: computed(() => props.disabled || props.readonly),
        sameWidth: false,
      })

      const disableDay = (day: Dayjs, start?: Dayjs|null, end?: Dayjs|null) => {
        if (!props.allowEqual && isDayEqual(startState.value, day)) {
          return true
        }
        if (startState.value && !endState.value && isDayAfter(startState.value, day)) {
          return true
        }
        if (props.disableDay) {
          return props.disableDay(day, start, end)
        }
        return false
      }

      const handleClick = (obj: Dayjs|null) => {
        const val = setModel(obj)
        if (!val) {
          return
        }
        shortcut.value = null
        if (!isDayEqual(val.start, startState.value)) {
          emit('update:start', val.start)
        }
        if (!isDayEqual(val.end, endState.value)) {
          emit('update:end', val.end)
          if (val.end && props.selectMode !== 'time') {
            toggle(false)
          }
        }
      }

      function handleSelectTime(direction: string, timeObj: TimeObjType) {
        if (direction === 'start') {
          startTime.setObj(timeObj)
          const day = (startState.value || dayjs())
            .hour(timeObj.hour)
            .minute(timeObj.minute)
            .second(timeObj.second)

          const val = toModel(day, props.selectMode)
          emit('update:start', val)
        } else {
          endTime.setObj(timeObj)
          const day = (endState.value || dayjs())
            .hour(timeObj.hour)
            .minute(timeObj.minute)
            .second(timeObj.second)

          const val = toModel(day, props.selectMode)
          emit('update:end', val)
        }
      }

      const getInputProps = (direction: 'left'|'right') => ({
        placeholder: direction === 'left' ? props.startPlaceholder : props.endPlaceholder,
        onFocus: () => {
          if (direction === 'left' && startState.value) {
            base.value = startState.value
          }
          if (direction === 'right' && endState.value) {
            if (startState.value?.month() !== endState.value.month()) {
              base.value = endState.value.subtract(1, 'month')
            } else {
              base.value = startState.value
            }
          }
        },
        onBlur: (e: KeyboardEvent) => {
          const val = (e.target as HTMLInputElement).value.trim()
          const compareStr = direction === 'left' ? startStr.value : startStr.value
          if (val === compareStr) {
            return
          }
          shortcut.value = null
          if (!val) {
            if (direction === 'left') {
              emit('update:start', null)
            } else {
              emit('update:end', null)
            }
            return
          }
          (e.target as HTMLInputElement).value = compareStr
          const dayObj = dayjs(val)
          if (dayObj.isValid()) {
            const start = direction === 'left' ? null : startState.value
            if (disableDay(dayObj, start)) {
              return
            }
            if (direction === 'left') {
              emit('update:start', toModel(dayObj, props.selectMode))
            } else {
              emit('update:end', toModel(dayObj, props.selectMode))
            }
          }
        },
      })

      const handleClear = () => {
        handleClick(null)
        toggle(false)
      }

      const handleMouseOver = (dateObj: Dayjs, { disabled = false }) => {
        if (disabled
          || (!props.allowEqual
            && (
              isDayEqual(dateObj, startState.value)
              || isDayEqual(dateObj, endState.value)
            )
          )
        ) {
          hoverDate.value = null
          return
        }
        hoverDate.value = dateObj
      }

      let bindProps = {}
      if (props.selectMode === 'year') {
        bindProps = {
          defaultMode: 'y',
          onSetViewMode: (mode: ViewModeType, baseObj: Dayjs) => {
            if (mode === 'm') {
              handleClick(baseObj)
              return 'year'
            }
            return mode
          },
        }
      } else if (props.selectMode === 'month') {
        bindProps = {
          defaultMode: 'm',
          onSetViewMode: (mode: ViewModeType, baseObj: Dayjs) => {
            if (mode === 'd') {
              handleClick(baseObj)
              return 'm'
            }
            return mode
          },
        }
      }

      return {
        CALENDAR_14,
        base,
        startStr,
        endStr,
        targetRef,
        popperRef,
        isActive,
        toggle,
        getInputProps,
        handleClear,
        shortcutList,
        shortcut,
        startTime,
        endTime,
        handleSelectTime,
        pickShortcut(name: string, { start } : { start: Dayjs }) {
          shortcut.value = name
          const { start: startValue, end: endValue } = toRangeModel(start, now)
          emit('update:start', startValue)
          emit('update:end', endValue)
        },
        bindContainerProps: computed(() => {
          let selected
          let startDay
          let endDay
          if (
            (startState.value && !endState.value)
            || (!startState.value && endState.value)
          ) {
            const val = startState.value || endState.value
            if (!hoverDate.value) {
              selected = [val]
            } else if (val && hoverDate.value > val) {
              startDay = val
              endDay = hoverDate.value
            } else {
              selected = [val]
            }
          } else {
            startDay = startState.value
            endDay = endState.value
          }
          return {
            ...bindProps,
            disableDay,
            handleMouseOver,
            onClickDay: handleClick,
            selected,
            startDay,
            endDay,
          }
        }),
      }
    },
  })
</script>
